import React, { useState, useEffect, useCallback } from 'react';
import { Loader, RefreshCw, Check } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import { logVipOutreach } from '../services/api';
import { draftIntoOutlook } from '../lib/outreachDraft';
import { buildDeliveredFollowupEmail } from '../lib/deliveredFollowupEmail';

// THE DELIVERED BY DAY FOLLOW-UP - a second note, with a 72-hour deadline, to the ones who never
// answered the offer. Greg, 2026-10-27. The rules are all server side (netlify/functions/lib/
// delivered-followup.js); this screen shows a rep her own list and writes the drafts.
//
// ⚠️ A REP SEES HER OWN LIST AND NOBODY ELSE'S. The follow-up is a reply on the first email, and
// that message is in the mailbox that sent it. Same as the win-back follow-up (VipWinbackFollowup).
// A customer moved from another rep's list is on the new rep's list, and goes as a new email.
//
// ⚠️ THE DEADLINE IS TODAY PLUS THREE, AND IT IS WRITTEN INTO THE DRAFT. A draft written today and
// sent on Thursday still says today's date, so the screen says to send them the same day.

const fmtDate = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const firstName = (s) => String(s || '').trim().split(/\s+/)[0] || 'rep';
const money = (n) => '$' + Number(n).toFixed(2);

// Three Graph calls a draft when it is threaded; see VipWinbackFollowup for the measurement.
const DRAFT_CHUNK = 6;

async function call(action, params = {}) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base + '/vip-winback', { method: 'POST', headers, body: JSON.stringify({ action, ...params }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'follow-up ' + res.status);
  return res.json();
}

const BTN = (bg, border, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
  fontSize: 12, fontWeight: 600, background: bg, border: '1px solid ' + border, color,
  cursor: 'pointer', fontFamily: 'inherit',
});

export default function DeliveredFollowup({ rep }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try { setData(await call('delivered-followup')); setErr(null); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // The whole queue goes in with a target, so a customer the send refuses (a handwritten note from
  // another rep, say) is replaced by the next one rather than leaving her at 48.
  const draft = useCallback(async (rows, target) => {
    setMsg(null);
    setDrafting({ done: 0, total: target });
    try {
      const res = await draftIntoOutlook(rows.map((c) => {
        const built = buildDeliveredFollowupEmail(c, rep);
        return {
          key: c.customerId,
          // The server takes the address and the price from the FIRST send and ignores these.
          claim: (track) => logVipOutreach({
            customerId: c.customerId, email: c.email, orderNumber: c.orderNumber,
            wouldHaveSaved: c.wouldHaveSaved, offerPrice: c.offerPrice,
            adminId: rep.adminId, repName: rep.name, repEmail: rep.email,
            rangeStart: null, rangeEnd: null,
            // The deadline this customer was given, so a late "I'm in" can be checked against it.
            mode: 'delivered_followup', category: 'deadline:' + c.claimBy,
            sendToken: track.token, pixelEmbedded: track.embedded,
          }),
          build: () => ({ subject: built.subject, html: built.html }),
        };
      }), { chunk: DRAFT_CHUNK, target, onProgress: (done, total) => setDrafting({ done, total }) });
      setMsg(res);
      load();
    } catch (e) {
      setMsg({ error: e.message });
    } finally { setDrafting(null); }
  }, [rep, load]);

  if (!data) {
    return err
      ? <div style={{ fontSize: 13, color: '#a12020', padding: 12 }}>{err}</div>
      : <div style={{ padding: 24, color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading the follow-up list...</div>;
  }

  const { summary, queue, cap, me } = data;
  const st = summary.states || {};
  const isLead = !!(me && me.isLead);
  const batch = Math.min(cap.left, queue.length);
  const movedRows = queue.filter((c) => c.movedFrom);
  const movedNames = [...new Set(movedRows.map((c) => c.movedFrom))].join(' and ');
  const setAside = [
    [st.member, 'joined since'],
    [st.replied, 'wrote back'],
    [st.later_offer, 'were sent another offer since'],
    [st.skipped, 'set aside or Do not contact'],
    [st.bounced, 'bounced the first time'],
    [st.never_sent, 'never got the first email'],
    [st.too_soon, 'emailed under a week ago'],
    [st.no_email, 'no address'],
  ].filter(([n]) => n > 0);

  return (
    <div>
      {err && <div style={{ fontSize: 12.5, color: '#a12020', marginBottom: 8 }}>{err}</div>}

      <div style={{ background: '#fff8f3', border: '1px solid #f0c9b4', borderRadius: 8, padding: '12px 14px', marginBottom: 14, fontSize: 13, lineHeight: 1.6, color: 'var(--gray-800)' }}>
        <b style={{ color: 'var(--navy)' }}>A last chance at the offer, with 72 hours on it.</b>{' '}
        Each one goes out as a reply on your first email, at the same price they were offered (the free ones stay free).
        Drafts written today say the offer is held until <b>{cap.claimByLabel}</b>, so <b>send them today</b>.
        Tomorrow's drafts will carry tomorrow's date. Anyone who replies "no thanks" comes off the VIP offers on their own.
        {movedRows.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <b style={{ color: 'var(--navy)' }}>Some of these came over from {movedNames}.</b>{' '}
            They come after your own. The first email is not in your mailbox, so each goes as a new email that
            says {movedNames} wrote first. Replies come to you, and you set up the membership.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: cap.left > 0 ? '#f7fafc' : '#fff8f3', border: '1px solid ' + (cap.left > 0 ? 'var(--gray-200)' : 'var(--orange)'), borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
        <div style={{ fontSize: 13 }}>
          <b style={{ color: 'var(--navy)', fontSize: 15 }}>{cap.used} of {cap.cap}</b>
          <span style={{ color: 'var(--gray-600)' }}> follow-ups by you today</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--gray-600)' }}>
          {queue.length >= 200 ? '200 or more' : queue.length} of yours still to go
        </div>
        {cap.teamUsed > 0 && <div style={{ fontSize: 12.5, color: 'var(--gray-600)' }}>Team {cap.teamUsed} today</div>}
        {!cap.enabled && <span style={{ fontSize: 12.5, color: 'var(--orange)', fontWeight: 600 }}>Follow-ups are switched off right now.</span>}
        {cap.enabled && cap.left <= 0 && <span style={{ fontSize: 12.5, color: 'var(--orange)', fontWeight: 600 }}>That is your {cap.cap} for today. The rest keep until tomorrow.</span>}
        {isLead && (
          <button
            onClick={async () => {
              const v = window.prompt('How many Delivered by day follow-ups can each person send in a day?', String(cap.cap));
              if (v === null || !/^\d+$/.test(v.trim())) return;
              try { await call('delivered-followup-config', { cap: Number(v) }); load(); } catch (e) { setErr(e.message); }
            }}
            style={{ border: 'none', background: 'none', color: 'var(--orange)', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
          >change the daily number</button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={load} disabled={busy} style={BTN('#fff', 'var(--gray-300)', 'var(--gray-700)')}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {isLead && (
        <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, background: '#fff', fontSize: 12.5, color: 'var(--gray-700)' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: setAside.length ? 8 : 0 }}>
            {summary.reps.map((r) => (
              <div key={r.adminId} style={{ border: '1px solid var(--gray-200)', borderRadius: 8, padding: '8px 12px', minWidth: 170, background: '#fcfdfe' }}>
                <b style={{ color: 'var(--navy)', fontSize: 13.5 }}>{firstName(r.name)}</b>
                <div><b style={{ color: 'var(--navy)' }}>{r.ready}</b> still to follow up</div>
                <div style={{ color: 'var(--gray-500)' }}>{r.followed} done{r.tooSoon ? `, ${r.tooSoon} not a week old yet` : ''}</div>
                {r.movedIn > 0 && <div style={{ color: 'var(--gray-500)' }}>{r.movedIn} of these moved from another list</div>}
              </div>
            ))}
          </div>
          {setAside.length > 0 && (
            <div style={{ color: 'var(--gray-500)' }}>
              Not being followed up: {setAside.map(([n, why]) => n + ' ' + why).join(', ')}.
            </div>
          )}
        </div>
      )}

      {queue.length > 0 && batch > 0 && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: '#eef4ff', border: '1px solid #c7d7fe', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          <button onClick={() => draft(queue, batch)} disabled={!!drafting}
            style={{ ...BTN('var(--navy)', 'var(--navy)', '#fff'), opacity: drafting ? 0.6 : 1 }}>
            {drafting ? `Writing ${drafting.done} of ${drafting.total}...` : `Put ${batch} follow-ups in my Outlook drafts`}
          </button>
          <span style={{ fontSize: 12.5, color: 'var(--gray-600)', lineHeight: 1.5 }}>
            {movedRows.length === queue.length
              ? 'Each one appears in Outlook as a new email.'
              : movedRows.length > 0 ? 'Your own appear in Outlook as replies to your first email, and moved ones as new emails.'
                : 'Each one appears in Outlook as a reply to your first email.'}{' '}
            Read it and press Send. Nothing is sent for you.
          </span>
        </div>
      )}

      {msg && (
        <div style={{ fontSize: 13, lineHeight: 1.6, background: msg.error ? '#fff5f5' : '#f0fff4', border: '1px solid ' + (msg.error ? '#fc8181' : '#9ae6b4'), borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
          {msg.error ? (
            <b style={{ color: '#c53030' }}>Could not write the drafts: {msg.error}</b>
          ) : (
            <>
              <b style={{ color: 'var(--navy)' }}>
                {msg.created} {msg.created === 1 ? 'follow-up is' : 'follow-ups are'} in your Outlook drafts{msg.mailbox ? ' (' + msg.mailbox + ')' : ''}.
              </b>{' '}
              {msg.moved > 0 && <span>{msg.moved} moved from another list {msg.moved === 1 ? 'is a new email' : 'are new emails'}. </span>}
              {msg.created - (msg.moved || 0) > msg.threaded && (
                <span>{msg.created - (msg.moved || 0) - msg.threaded} could not be attached to the first email, so {msg.created - (msg.moved || 0) - msg.threaded === 1 ? 'it is' : 'they are'} written as a new message instead. </span>
              )}
              Open Outlook, read each one and press Send today.
              {(msg.blocked || []).length > 0 && (
                <div style={{ marginTop: 6, color: 'var(--gray-600)' }}>
                  {msg.blocked.length} skipped: {msg.blocked.slice(0, 3).map((b) => b.reason).join('; ')}
                  {msg.blocked.length > 3 ? ' and others' : ''}
                </div>
              )}
              {((msg.failed || []).length + (msg.skipped || []).length) > 0 && (
                <div style={{ marginTop: 6, color: '#c53030' }}>
                  {(msg.failed || []).length + (msg.skipped || []).length} could not be written to Outlook
                  {(msg.failed || [])[0] && msg.failed[0].error ? ' (' + msg.failed[0].error + ')' : ''}.
                  {' '}They do not count against your day and are back on your list, so you can press the button again.
                </div>
              )}
            </>
          )}
          <button onClick={() => setMsg(null)} style={{ ...BTN('#fff', 'var(--gray-300)', 'var(--gray-600)'), marginLeft: 10 }}>Dismiss</button>
        </div>
      )}

      {queue.length === 0 ? (
        <div style={{ padding: 28, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13.5 }}>
          Nobody of yours is waiting on a follow-up.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="orders-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--gray-200)' }}>
                {['Customer', 'Offered', 'Their offer', ''].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {queue.map((c) => (
                <tr key={c.customerId} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '9px 10px' }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.name}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>{c.email}</div>
                  </td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>{fmtDate(c.firstSent)}</td>
                  <td style={{ padding: '9px 10px', color: 'var(--gray-600)', whiteSpace: 'nowrap' }}>{c.offerPrice > 0 ? money(c.offerPrice) : 'Free year'}</td>
                  <td style={{ padding: '9px 10px', fontSize: 11.5, color: 'var(--gray-500)' }}>
                    {c.opened && <><Check size={11} style={{ verticalAlign: -1 }} /> opened the first one</>}
                    {c.movedFrom && <div>From {c.movedFrom}'s list, new email</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {queue.length >= 200 && (
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', padding: 8 }}>Showing your next 200. The ones who opened the first email come first.</div>
          )}
        </div>
      )}
    </div>
  );
}
